import { useState, useMemo, useCallback } from 'react';
import {
  buildDraft,
  cloneDraft,
  draftDiff,
  cycleCellDraft,
  venueDraftDiffers,
} from '../lib/draft';
import { updateVenueAgeRestrictions } from '../api/venueAgeRestrictions';
import { toastSuccess, toastError } from '../../../shared/toastHelper';

const useMatrixDraft = ({
  teamId,
  csrfToken,
  venueOwnerships,
  initialVenueAgeRestrictions,
}) => {
  const [savedDraft, setSavedDraft] = useState(() =>
    buildDraft(venueOwnerships, initialVenueAgeRestrictions || {})
  );
  const [draft, setDraft] = useState(() => cloneDraft(savedDraft));
  const [saving, setSaving] = useState(false);

  const isDirty = useMemo(() => draftDiff(draft, savedDraft), [draft, savedDraft]);

  const cycleCell = useCallback((venueId, optionId) => {
    setDraft((prev) => cycleCellDraft(prev, venueId, optionId));
  }, []);

  const cancel = useCallback(() => {
    setDraft(cloneDraft(savedDraft));
  }, [savedDraft]);

  const pruneSetting = useCallback((settingId) => {
    const prune = (input) => {
      const next = cloneDraft(input);
      Object.values(next).forEach((cfg) => {
        cfg.enabled.delete(settingId);
        if (cfg.defaultId === settingId) cfg.defaultId = null;
      });
      return next;
    };
    setDraft(prune);
    setSavedDraft(prune);
  }, []);

  const saveAll = useCallback(async () => {
    setSaving(true);

    const venuesToSave = venueOwnerships.filter((vo) =>
      venueDraftDiffers(draft[vo.id], savedDraft[vo.id])
    );

    const results = await Promise.allSettled(
      venuesToSave.map((vo) =>
        updateVenueAgeRestrictions(
          teamId,
          vo.id,
          {
            enabled_setting_ids: Array.from(draft[vo.id].enabled),
            default_setting_id: draft[vo.id].defaultId,
          },
          csrfToken
        ).then(() => vo.id)
      )
    );

    const succeeded = new Set(
      results.filter((r) => r.status === 'fulfilled').map((r) => r.value)
    );

    setSavedDraft((prev) => {
      const next = cloneDraft(prev);
      venuesToSave.forEach((vo) => {
        if (succeeded.has(vo.id)) {
          next[vo.id] = {
            enabled: new Set(draft[vo.id].enabled),
            defaultId: draft[vo.id].defaultId,
          };
        }
      });
      return next;
    });

    const failures = results.filter((r) => r.status === 'rejected');
    if (failures.length > 0) {
      const firstError = failures[0].reason?.response?.data?.errors?.[0];
      toastError(
        firstError ||
          `Failed to save ${failures.length} venue${failures.length > 1 ? 's' : ''}.`
      );
    } else if (venuesToSave.length > 0) {
      toastSuccess('Age restrictions updated');
    }

    setSaving(false);
  }, [teamId, csrfToken, venueOwnerships, draft, savedDraft]);

  return {
    draft,
    isDirty,
    saving,
    cycleCell,
    saveAll,
    cancel,
    pruneSetting,
  };
};

export default useMatrixDraft;
